<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'

  import { useFirebaseApp } from 'vuefire'

  import { getFunctions, httpsCallable } from 'firebase/functions'

  import { SERVICE_PUBLISH_REVIEWERS, STAGE_VERSION_NAMES, isAcceptedStage } from '#ourahub-intra/constants'

  import { gcpRegion } from '#vue-shared-lib/utils/cloud-env'

  import type { Service } from '#ourahub-intra/types/services'

  const props = withDefaults(
    defineProps<{
      modelValue: boolean
      service: Service
      stage?: 'sandbox' | 'staging' | 'release'
    }>(),
    { stage: 'sandbox' },
  )

  const emits = defineEmits<{
    'update:modelValue': [value: boolean]
  }>()

  const isLoading = ref(false)
  const result = ref<'success' | 'error' | null>(null)

  const firebaseApp = useFirebaseApp()

  const replacedStage = computed(() =>
    props.service.public ? STAGE_VERSION_NAMES[props.service.public as keyof typeof STAGE_VERSION_NAMES] : '',
  )

  const needsReview = computed(() => !isAcceptedStage(props.service, props.stage))

  const isFirstPublish = computed(() => !props.service.acceptedFor)

  watch(
    () => props.modelValue,
    (open) => {
      if (open) {
        result.value = null
      }
    },
  )

  async function requestPublish() {
    try {
      isLoading.value = true

      await httpsCallable(
        getFunctions(firebaseApp, gcpRegion),
        'requestPublishServiceAsPublic',
      )({
        serviceId: props.service.id,
        stage: props.stage,
      })

      result.value = 'success'
    } catch (e: unknown) {
      console.error(e)
      result.value = 'error'
    } finally {
      isLoading.value = false
    }
  }
</script>

<template lang="html">
  <v-dialog max-width="600" :model-value="modelValue" @update:model-value="emits('update:modelValue', $event)">
    <v-card>
      <v-card-title>Select {{ STAGE_VERSION_NAMES[stage] }} as the public version</v-card-title>

      <v-card-text>
        <p>
          You are about to {{ needsReview ? 'request selecting' : 'select' }}
          <b>{{ STAGE_VERSION_NAMES[stage] }}</b>
          as the public version of
          <b>{{ service.name }}.</b>
        </p>

        <p>
          This will
          <b>make the {{ STAGE_VERSION_NAMES[stage] }} version of app / tool accessible to all OuraHub users</b>
          with required permissions. The app / tool will also be listed in the OuraHub catalog.
        </p>

        <template v-if="needsReview">
          <p>
            The following reviewers will be sent the request in Slack. After one of them approves, the app / tool will
            have {{ STAGE_VERSION_NAMES[stage] }} as its public version.
          </p>

          <ul>
            <li v-for="reviewer in SERVICE_PUBLISH_REVIEWERS" :key="reviewer">{{ reviewer }}</li>
          </ul>
        </template>

        <p v-else>
          <b>{{ STAGE_VERSION_NAMES[stage] }}</b>
          has already been reviewed, so this happens straight away with no new review.
        </p>
      </v-card-text>

      <o-alert v-if="!result && replacedStage" type="warning" class="mx-4 mb-4">
        This will make
        <b>{{ replacedStage }}</b>
        private (for maintainers only).
      </o-alert>

      <o-alert v-if="!result && isFirstPublish" type="info" class="mx-4 mb-4">
        Since this is the first public version of the app / tool, an automatic announcement will be sent to
        <b>#help-oura-tooling</b>
        once the request is approved.
      </o-alert>

      <o-alert v-if="result === 'success'" type="success" class="mx-4 mb-4">
        <template v-if="needsReview">
          Your public version request has been sent. You'll get notified in Slack once a reviewer approves or denies it.
        </template>
        <template v-else>{{ STAGE_VERSION_NAMES[stage] }} is now the public version of {{ service.name }}.</template>
      </o-alert>

      <o-alert v-else-if="result === 'error'" type="error" class="mx-4 mb-4">
        Selecting the public version failed. Please report this in
        <b>#help-oura-tooling.</b>
      </o-alert>

      <v-card-actions>
        <v-spacer />
        <o-btn-text :text="result ? 'Close' : 'Cancel'" @click="emits('update:modelValue', false)" />
        <o-btn-primary
          v-if="!result"
          prepend-icon="mdi-publish"
          :loading="isLoading"
          :text="needsReview ? 'Send request' : 'Select as public'"
          @click="requestPublish()"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
